import { twMerge } from "tailwind-merge"

import {
  Box,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

export type SliderAccent = "blue" | "purple" | "yellow"

const SLIDER_ACCENT_CLASS = {
  blue: {
    filledTrack:
      "bg-blue-500 data-[focus=true]:!bg-blue-500 data-[active=true]:!bg-blue-500 data-[hover=true]:!bg-blue-500",
    thumb:
      "bg-blue-500 data-[focus=true]:!bg-blue-500 data-[active=true]:!bg-blue-500 data-[hover=true]:!bg-blue-500 web:data-[focus=true]:outline-blue-400 web:data-[active=true]:outline-blue-400",
  },
  purple: {
    filledTrack:
      "bg-purple-500 data-[focus=true]:!bg-purple-500 data-[active=true]:!bg-purple-500 data-[hover=true]:!bg-purple-500",
    thumb:
      "bg-purple-500 data-[focus=true]:!bg-purple-500 data-[active=true]:!bg-purple-500 data-[hover=true]:!bg-purple-500 web:data-[focus=true]:outline-purple-400 web:data-[active=true]:outline-purple-400",
  },
  yellow: {
    filledTrack:
      "bg-yellow-500 data-[focus=true]:!bg-yellow-500 data-[active=true]:!bg-yellow-500 data-[hover=true]:!bg-yellow-500",
    thumb:
      "bg-yellow-500 data-[focus=true]:!bg-yellow-500 data-[active=true]:!bg-yellow-500 data-[hover=true]:!bg-yellow-500 web:data-[focus=true]:outline-yellow-400 web:data-[active=true]:outline-yellow-400",
  },
} as const satisfies Record<SliderAccent, { filledTrack: string; thumb: string }>

const SLIDER_THUMB_CLASS =
  "h-9 w-11 px-4 py-2 shadow-none ring-2 ring-background-primary web:outline-none web:data-[focus=true]:outline web:data-[focus=true]:!outline-1 web:data-[active=true]:outline web:data-[active=true]:!outline-1"

const SLIDER_TRACK_CLASS =
  "h-2 rounded-full bg-background-tertiary data-[focus=true]:!bg-background-tertiary data-[active=true]:!bg-background-tertiary web:outline-none"

export type SliderFormFieldProps = {
  label: React.ReactNode
  displayValue: string
  value: number
  onChange: (value: number) => void
  minValue: number
  maxValue: number
  step: number
  minLabel: string
  maxLabel: string
  accent: SliderAccent
  className?: string
}

export function SliderFormField({
  label,
  displayValue,
  value,
  onChange,
  minValue,
  maxValue,
  step,
  minLabel,
  maxLabel,
  accent,
  className,
}: SliderFormFieldProps) {
  const accentClass = SLIDER_ACCENT_CLASS[accent]

  return (
    <VStack className={twMerge("gap-0", className)}>
      <Box className="items-center px-8 py-6">
        {/* rounded-lg border border-lining-medium bg-background-secondary */}
        <Text className="text-sm text-foreground-tertiary">{label}</Text>
        <Text className="text-lg text-foreground-secondary">{displayValue}</Text>
      </Box>

      <Box className="px-3">
        <Slider
          value={value}
          onChange={onChange}
          minValue={minValue}
          maxValue={maxValue}
          step={step}
          className="py-6"
        >
          <SliderTrack className={SLIDER_TRACK_CLASS}>
            <SliderFilledTrack className={accentClass.filledTrack} />
          </SliderTrack>
          <SliderThumb className={twMerge(SLIDER_THUMB_CLASS, accentClass.thumb)}>
            <SliderGrip />
          </SliderThumb>
        </Slider>
        <HStack className="justify-between">
          <Text className="text-xs text-foreground-tertiary">{minLabel}</Text>
          <Text className="text-xs text-foreground-tertiary">{maxLabel}</Text>
        </HStack>
      </Box>
    </VStack>
  )
}

export function formatPercentLabel(value: number): string {
  if (value === 0) {
    return "0%"
  }

  if (value < 1) {
    return `${value.toFixed(1)}%`
  }

  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1).replace(/\.0$/, "")}%`
}

function SliderGrip() {
  return (
    <VStack className="gap-1">
      {[0, 1, 2].map((row) => (
        <HStack
          key={row}
          className="gap-1"
        >
          <Box className="h-1 w-1 rounded-full bg-background-primary" />
          <Box className="h-1 w-1 rounded-full bg-background-primary" />
        </HStack>
      ))}
    </VStack>
  )
}
